import React from 'react';
import { Avatar, Badge, IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material';

interface UserAvatarProps {
    user?: {
        id?: number | string;
        username?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        profilePicture?: string;
    };
    size?: number;
    editable?: boolean;
    onEdit?: () => void;
    showBadge?: boolean;
    onClick?: () => void;
}

// Generate consistent colors based on user ID or name
const stringToColor = (string: string): string => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 50%)`;
};

// Generate initials from name
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
    if (firstName && lastName) {
        return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
        // For single names, use first 2 letters
        return firstName.slice(0, 2).toUpperCase();
    }
    if (lastName) {
        // If only lastName is provided, use first 2 letters
        return lastName.slice(0, 2).toUpperCase();
    }
    if (username) {
        // For usernames, use first 2 letters
        return username.slice(0, 2).toUpperCase();
    }
    return 'U';
};

// Generate avatar pattern
const generateAvatarPattern = (userId?: number | string, username?: string): string => {
    // Seed for future pattern variations: userId || username?.charCodeAt(0) || 1
    // Pattern types for future enhancement
    // const patterns = ['polka', 'stripes', 'waves', 'circles', 'squares', 'triangles'];
    // const pattern = patterns[seed % patterns.length];
    
    // Return a simple gradient for now, can be enhanced with actual patterns
    const color1 = stringToColor(username || String(userId) || 'default');
    const color2 = stringToColor((username || '') + 'secondary');
    
    return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
};

const UserAvatar: React.FC<UserAvatarProps> = ({
    user,
    size = 40,
    editable = false,
    onEdit,
    showBadge = false,
    onClick
}) => {
    const initials = getInitials(user?.firstName, user?.lastName, user?.username);
    const backgroundColor = user?.profilePicture 
        ? undefined 
        : stringToColor(user?.username || user?.email || 'default');
    
    const backgroundImage = user?.profilePicture 
        ? undefined 
        : generateAvatarPattern(user?.id, user?.username);

    const avatarContent = (
        <Avatar
            src={user?.profilePicture}
            sx={{
                width: size,
                height: size,
                backgroundColor: !user?.profilePicture ? backgroundColor : undefined,
                backgroundImage: !user?.profilePicture ? backgroundImage : undefined,
                fontSize: size * 0.4,
                fontWeight: 'bold',
                cursor: onClick ? 'pointer' : 'default',
                border: '2px solid',
                borderColor: 'background.paper',
                boxShadow: 2,
                transition: 'all 0.3s ease',
                '&:hover': onClick ? {
                    transform: 'scale(1.05)',
                    boxShadow: 4,
                } : {}
            }}
            onClick={onClick}
        >
            {!user?.profilePicture && (initials || <PersonIcon />)}
        </Avatar>
    );

    if (editable && onEdit) {
        return (
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                    <Tooltip title="Edit profile picture">
                        <IconButton
                            size="small"
                            onClick={onEdit}
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                width: size * 0.3,
                                height: size * 0.3,
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                },
                            }}
                        >
                            <EditIcon sx={{ fontSize: size * 0.15 }} />
                        </IconButton>
                    </Tooltip>
                }
            >
                {avatarContent}
            </Badge>
        );
    }

    if (showBadge) {
        return (
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
                color="success"
            >
                {avatarContent}
            </Badge>
        );
    }

    return avatarContent;
};

export default UserAvatar;